import React from "react";
import { Link } from "react-router-dom";
import "./index.css";

const navLinks = [
  { title: "Home", href: "#home" },
  { title: "About Us", href: "#about" },
  { title: "Our Business", href: "#services" },
  { title: "Gallery", href: "#gallery" },
  { title: "Contact Us", href: "#contact" },
];

class Navbar extends React.Component {
  state = {
    isMenuOpen: false,
    scrolled: false,
    activeSection: "home",
  };

  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll);
    this.handleScroll();
    this.initScrollSpy();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.isMenuOpen !== this.state.isMenuOpen) {
      if (this.state.isMenuOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("scroll", this.handleScrollSpy);
    document.body.style.overflow = "unset";
  }

  handleScroll = () => {
    this.setState({ scrolled: window.scrollY > 50 });
  };

  initScrollSpy = () => {
    this.handleScrollSpy();
    window.addEventListener("scroll", this.handleScrollSpy);
  };

  handleScrollSpy = () => {
    // Get all sections
    const sections = navLinks.map((link) => link.href.replace("#", ""));

    // Important: Check sections in reverse order (bottom to top)
    // This ensures lower sections on the page are detected first
    for (let i = sections.length - 1; i >= 0; i--) {
      const section = sections[i];
      const element = document.getElementById(section);
      if (!element) continue;

      const rect = element.getBoundingClientRect();
      // Adjust this offset based on your navbar height
      const topOffset = 80;

      // A section is considered "active" when its top is near the top of the viewport
      if (rect.top <= topOffset && rect.top > -0.5 * rect.height) {
        if (this.state.activeSection !== section) {
          this.setState({ activeSection: section });
        }
        return; // Stop checking once we find an active section
      }
    }

    // If no section is found active and we're at the very top, set home as active
    if (window.scrollY < 50) {
      if (this.state.activeSection !== "home") {
        this.setState({ activeSection: "home" });
      }
    }
  };

  toggleMenu = () => {
    this.setState((prevState) => ({ isMenuOpen: !prevState.isMenuOpen }));
  };

  handleNavLinkClick = (e) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute("href") || "";
    const targetId = href.replace("#", "");
    const element = document.getElementById(targetId);

    if (element) {
      const yOffset = -60; // Adjust based on reduced navbar height
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });

      // Update active section immediately
      this.setState({ activeSection: targetId });
    }

    // Close mobile menu if open
    if (this.state.isMenuOpen) {
      this.setState({ isMenuOpen: false });
    }
  };

  render() {
    const { isMenuOpen, scrolled, activeSection } = this.state;

    return (
      <nav
        className={`navbar-nav ${
          scrolled ? "navbar-scrolled" : "navbar-default"
        }`}
      >
        <div className="navbar-container">
          {/* Logo */}
          <Link to="/" className="navbar-logo">
            <img
              src="/Images/WLW_Logo_PNG.png"
              alt="W.L.W. Enterprise Logo"
              className="navbar-logo-img"
            />
          </Link>

          {/* Main Navigation */}
          <div className="navbar-desktop">
            {navLinks.map((link) => {
              const linkSection = link.href.replace("#", "");
              const isActive = activeSection === linkSection;
              const isHome = linkSection === "home";

              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={this.handleNavLinkClick}
                  className={`navbar-link ${
                    isActive ? "navbar-link-active" : ""
                  } ${isHome ? "navbar-link-home" : ""}`}
                >
                  {link.title}
                </a>
              );
            })}
          </div>

          {/* Contact Info */}
          <div className="navbar-contact">
            <div className="navbar-contact-info">
              <a href="tel:3309531005" className="navbar-phone">
                <span className="navbar-phone-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </span>
                (330) 967 8874
              </a>
              <a
                href="mailto:swlwenterprise@gmail.com"
                className="navbar-email"
              >
                <span className="navbar-email-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </span>
                swlwenterprise@gmail.com
              </a>
            </div>

            <a
              href="#appointment"
              onClick={this.handleNavLinkClick}
              className="navbar-cta"
            >
              Book a Consultation
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="navbar-mobile-toggle"
            onClick={this.toggleMenu}
            aria-label="Toggle menu"
          >
            <div className="navbar-toggle-button">
              <span
                className={`navbar-toggle-bar ${
                  isMenuOpen ? "navbar-toggle-bar-top" : ""
                }`}
              ></span>
              <span
                className={`navbar-toggle-bar ${
                  isMenuOpen ? "navbar-toggle-bar-middle" : ""
                }`}
              ></span>
              <span
                className={`navbar-toggle-bar ${
                  isMenuOpen ? "navbar-toggle-bar-bottom" : ""
                }`}
              ></span>
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="navbar-mobile">
            <div className="navbar-mobile-container">
              <div className="navbar-mobile-links">
                {navLinks.map((link) => {
                  const isActive = activeSection === link.href.replace("#", "");

                  return (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={this.handleNavLinkClick}
                      className={`navbar-mobile-link ${
                        isActive ? "navbar-mobile-link-active" : ""
                      }`}
                    >
                      {link.title}
                    </a>
                  );
                })}
              </div>

              <div className="navbar-mobile-contact">
                <a href="tel:3309531005" className="navbar-mobile-phone">
                  <span className="navbar-mobile-phone-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </span>
                  (330) 953 1005
                </a>
                <a
                  href="mailto:support@wlwenterprise.com"
                  className="navbar-mobile-email"
                >
                  <span className="navbar-mobile-email-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </span>
                  support@wlwenterprise.com
                </a>

                <a
                  href="#appointment"
                  onClick={this.handleNavLinkClick}
                  className="navbar-mobile-cta"
                >
                  Book a Consultation
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    );
  }
}

export default Navbar;
